import React from "react";
import { Link } from "react-router-dom";
import "../style.css";

export default function HelpPage() {
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "What are binaural beats?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Binaural beats occur when different frequencies play in each ear, creating an illusory third tone equal to their difference. For example, 210 Hz in the right ear and 200 Hz in the left creates a perceived 10 Hz beat, which falls in the alpha brainwave range associated with relaxed focus."
        }
      },
      {
        "@type": "Question",
        "name": "How do binaural beats work?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Your brainstem tries to reconcile the different frequencies, creating neural firing patterns matching the frequency difference. This activity can spread through the brain, potentially influencing overall brainwave patterns to synchronize with the beat frequency, shifting your mental state."
        }
      },
      {
        "@type": "Question",
        "name": "What are the benefits of binaural beats?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Binaural beats may help with relaxation, focus, meditation, sleep, and stress reduction. Different frequencies are associated with specific brainwave states, such as Delta for deep sleep and Gamma for cognitive processing."
        }
      },
      {
        "@type": "Question",
        "name": "How do I use this app?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "1. Select a beat based on your desired state. 2. (Optional) Select a noise for additional effects. 3. Add background sound by pasting a YouTube URL. 4. Adjust settings like volume and stereo width. 5. (Optional) Set a timer for timed sessions. 6. Save your favorite YouTube backgrounds for easy access later."
        }
      },
      {
        "@type": "Question",
        "name": "What are the tips for best results?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Use stereo headphones for the binaural effect to work properly. Start with short sessions (15-30 minutes) and gradually increase as comfortable. Find a quiet, comfortable space where you won't be disturbed. Combine with breathing techniques for enhanced effects. Experiment with different frequencies to find what works best for you."
        }
      },
      {
        "@type": "Question",
        "name": "What are the different types of noise available?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "The app provides three types of noise: White noise (equal energy across all frequencies, good for masking background noise), Pink noise (balanced sound energy, softer and more soothing), and Brown noise (emphasizes lower frequencies, deep and grounding)."
        }
      },
      {
        "@type": "Question",
        "name": "Is this app safe for everyone?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "This application is designed for general wellness purposes only. It is not intended to diagnose, treat, cure, or prevent any disease or health condition. If you have epilepsy, heart problems, or are pregnant, consult with a healthcare professional before using binaural beats."
        }
      }
    ]
  };

  return (
    <div className="app-container">
      <div className="help-content">
        <h1>Binaural Beats Guide</h1>
        
        <div className="help-section">
          <h2>What Are Binaural Beats?</h2>
          <p>
          Binaural beats occur when different frequencies play in each ear, creating an illusory third tone equal to their difference. For example, 210 Hz in the right ear and 200 Hz in the left creates a perceived 10 Hz beat, which falls in the alpha brainwave range associated with relaxed focus.
          </p>
          <p>
          This works because your brainstem tries to reconcile these different frequencies, creating neural firing patterns matching the frequency difference. This activity can spread through the brain, potentially influencing overall brainwave patterns to synchronize with the beat frequency, which may shift your mental state toward the corresponding brainwave pattern and its associated cognitive state.
          </p>
        </div>

        <div className="help-section">
          <h2>Brainwave Frequencies</h2>
          <div className="frequency-info">
            <div className="frequency-card indigo">
              <h3>Delta (1-4 Hz)</h3>
              <p>Associated with deep sleep, healing, and recovery. May help with insomnia and promoting restorative sleep.</p>
            </div>
            
            <div className="frequency-card purple">
              <h3>Theta (4-8 Hz)</h3>
              <p>Associated with deep relaxation, meditation, creativity, and REM sleep. May enhance creative thinking and meditation.</p>
            </div>
            
            <div className="frequency-card blue">
              <h3>Alpha (8-12 Hz)</h3>
              <p>Associated with relaxed focus, calmness, and positive thinking. May help reduce anxiety and promote relaxation.</p>
            </div>
            
            <div className="frequency-card green">
              <h3>Beta (12-30 Hz)</h3>
              <p>Associated with active thinking, focus, and alertness. May help improve concentration and cognitive performance.</p>
            </div>
            
            <div className="frequency-card orange">
              <h3>Gamma (30-100 Hz)</h3>
              <p>Associated with higher cognitive processing, learning, and peak performance. May enhance memory and information processing.</p>
            </div>

            <div className="frequency-card red">
              <h3>Vagus Nerve Stimulation (Delta Range)</h3>
              <p>Specific frequencies that may support vagus nerve function, promoting relaxation of the autonomic nervous system.</p>
            </div>
          </div>
        </div>

        <div className="help-section">
          <h2>Noise Options</h2>
          <p>
            In addition to binaural beats, this app provides three types of noise: white, pink, and brown. Each type of noise has unique characteristics and is used for different purposes based on scientific consensus.
          </p>
          <div className="noise-info">
            <div className="noise-card-help white">
              <h3>White Noise</h3>
              <p>
                White noise contains equal energy across all frequencies, creating a consistent "hissing" sound. It is commonly used to mask background noise, improve focus, and aid sleep by blocking distractions.
              </p>
              <p><strong>Best for:</strong> Studying, working, and sleeping in noisy environments.</p>
            </div>
            
            <div className="noise-card-help pink">
              <h3>Pink Noise</h3>
              <p>
                Pink noise balances sound energy naturally, with more emphasis on lower frequencies. It has a softer, more soothing sound compared to white noise. Studies suggest it may improve sleep quality and enhance memory.
              </p>
              <p><strong>Best for:</strong> Relaxation, improving sleep, and creating a calming atmosphere.</p>
            </div>
            
            <div className="noise-card-help brown">
              <h3>Brown Noise</h3>
              <p>
                Brown noise emphasizes even lower frequencies than pink noise, producing a deep, rumbling sound. It is often described as more natural and grounding. It may help with relaxation, reducing anxiety, and promoting deep sleep.
              </p>
              <p><strong>Best for:</strong> Calming the mind, reducing stress, and aiding deep sleep.</p>
            </div>
          </div>
        </div>

        <div className="help-section">
          <h2>How to Use This App</h2>
          <ol>
            <li><strong>Select a Beat:</strong> Choose from the preset binaural beats based on your desired state.</li>
            <li><strong>(Optional) Select a Noise:</strong> Choose from the preset noise based on your desired state.</li>
            <li><strong>Add Background Sound:</strong> Paste a YouTube URL for ambient music or nature sounds (optional).</li>
            <li><strong>Adjust Settings:</strong> Fine-tune the volume and stereo width to your preference.</li>
            <li><strong>(Optional) Set a Timer:</strong> Use the timer for timed sessions (meditation, focus, etc.).</li>
            <li><strong>Save Favorites:</strong> Save your favorite YouTube backgrounds for easy access later.</li>
          </ol>
        </div>

        <div className="help-section">
          <h2>Tips for Best Results</h2>
          <ul>
            <li>Use stereo headphones for the binaural effect to work properly.</li>
            <li>Keep the Stereo Width slider at its maximum for a true binaural beat — lowering it mixes both tones into each ear, shifting the effect toward a monaural beat.</li>
            <li>Start with short sessions (15-30 minutes) and gradually increase as comfortable.</li>
            <li>Find a quiet, comfortable space where you won't be disturbed.</li>
            <li>Combine with breathing techniques for enhanced effects.</li>
            <li>Experiment with different frequencies to find what works best for you.</li>
          </ul>
        </div>

        <div className="help-section">
          <h2>Research & Resources</h2>
          <p>
            Scientific studies have explored the potential benefits of binaural beats. Here are some research papers for further reading:
          </p>
          <div className="research-links">
            <a href="https://pmc.ncbi.nlm.nih.gov/articles/PMC10198548/" target="_blank" rel="noopener noreferrer" className="research-link">
              <h4>Effects of Binaural Beat Stimulation on Anxiety, Stress and Depression (2023)</h4>
              <p>Research examining the effects of binaural beat therapy on psychological states and potential therapeutic applications.</p>
            </a>
            
            <a href="https://pmc.ncbi.nlm.nih.gov/articles/PMC4231835/" target="_blank" rel="noopener noreferrer" className="research-link">
              <h4>The Effect of Binaural Beats on Working Memory and Cortical Connectivity (2014)</h4>
              <p>Study investigating how binaural beats influence cognitive performance and brain connectivity patterns.</p>
            </a>

            <a href="https://www.liebertpub.com/doi/abs/10.1089/acm.2006.6196" target="_blank" rel="noopener noreferrer" className="research-link">
              <h4>Binaural Beat Technology in Humans: A Pilot Study To Assess Psychologic and Physiologic Effects (2007)</h4>
              <p>Study on how Delta waves may reduce anxiety and improve quality of life.</p>
            </a>
            
            <a href="https://ieeexplore.ieee.org/abstract/document/10848724?casa_token=Qo3iXPHVOW0AAAAA:BwZucXNx1pC1Sf7t_eRbPpHrVqqjCfFHZBpWtVuq1DNdfv0T2Hx6QNr_SluF7KH0nZ9k26vj" target="_blank" rel="noopener noreferrer" className="research-link">
              <h4>Effect of Dynamic Binaural Beats on Concentration Enhancement (2024)</h4>
              <p>Dynamic binaural beats (Gamma) combined with rain sounds reduced HRV during mental arithmetic, suggesting improved concentration.</p>
            </a>

            <a href="https://www.frontiersin.org/journals/human-neuroscience/articles/10.3389/fnhum.2018.00387/full" target="_blank" rel="noopener noreferrer" className="research-link">
              <h4>A Novel Insight of Effects of a 3-Hz Binaural Beat on Sleep Stages During Sleep (2018)</h4>
              <p> 3-Hz binaural beat (Delta) increased slow-wave sleep (N3) duration and reduced its latency by enhancing delta activity, suggesting benefits for sleep quality and memory</p>
            </a>

          </div>
        </div>

        <div className="help-section">
          <div className="video-container">
            <iframe
              width="560"
              height="315"
              src="https://www.youtube.com/embed/iboQ7mmGGGU"
              title="YouTube video player"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            ></iframe>
          </div>
        </div>

        <div className="help-section disclaimer">
          <h2>Disclaimer</h2>
          <p>
            This application is designed for general wellness purposes only. It is not intended to diagnose, treat, cure, or prevent any disease or health condition. The effects of binaural beats may vary from person to person. If you have epilepsy, heart problems, or are pregnant, consult with a healthcare professional before using binaural beats.
          </p>
        </div>
        
        <div className="navigation-buttons">
          <Link to="/" className="button blue">Return to App</Link>
        </div>
      </div>
      <script type="application/ld+json">
        {JSON.stringify(faqSchema)}
      </script>
    </div>
  );
}